import { Controller } from "@hotwired/stimulus"
import { speechLevelTrace } from "../lib/speech/level_trace"

/**
 * Draws the shared microphone level trace onto its own <canvas>: a mirrored
 * wave, oldest sample at the left, older columns fading out. It is the room's
 * own sound, so nothing has to be read to know the mic is live.
 *
 * The element is the canvas: colour comes from its computed `color` (any
 * text-* utility), size from its layout box, and it draws wherever it is
 * connected — behind the conversation dock, at the foot of the transcript
 * drawer — because the trace it reads is one module-level buffer the capture
 * controller pushes into.
 */
export default class extends Controller<HTMLCanvasElement> {
  private unsubscribe: (() => void) | null = null
  private pending = false

  connect() {
    this.unsubscribe = speechLevelTrace.subscribe(this.schedulePaint)
    this.paint()
  }

  disconnect() {
    this.unsubscribe?.()
    this.unsubscribe = null
  }

  // Frames arrive far faster than a screen paints: draw at most once per
  // animation frame, from whatever the trace holds by then.
  private schedulePaint = () => {
    if (this.pending) return
    this.pending = true
    window.requestAnimationFrame(() => {
      this.pending = false
      if (this.element.isConnected) this.paint()
    })
  }

  private paint() {
    const canvas = this.element
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    if (width === 0 || height === 0) return

    const ratio = window.devicePixelRatio || 1
    if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
      canvas.width = Math.round(width * ratio)
      canvas.height = Math.round(height * ratio)
    }
    const context = canvas.getContext("2d")
    if (!context) return

    context.setTransform(ratio, 0, 0, ratio, 0, 0)
    context.clearRect(0, 0, width, height)
    context.fillStyle = window.getComputedStyle(canvas).color

    const samples = speechLevelTrace.size
    const columnWidth = width / samples
    const barWidth = Math.max(1, columnWidth * WAVE_COLUMN_FILL)
    const middle = height / 2
    // A quiet room still draws a line, and the line keeps its weight on a
    // canvas the size of the dictation surface rather than fading to dots.
    const minBar = Math.max(WAVE_MIN_HEIGHT_PX, height * WAVE_MIN_HEIGHT_RATIO)
    for (let column = 0; column < samples; column++) {
      const level = speechLevelTrace.at(column)
      const barHeight = Math.max(minBar, level * height * WAVE_HEIGHT_SCALE)
      context.globalAlpha = WAVE_MIN_ALPHA + (1 - WAVE_MIN_ALPHA) * (column / samples) ** 2
      context.fillRect(column * columnWidth, middle - barHeight / 2, barWidth, barHeight)
    }
    context.globalAlpha = 1
  }
}

const WAVE_COLUMN_FILL = 0.6
const WAVE_HEIGHT_SCALE = 0.8
const WAVE_MIN_HEIGHT_PX = 2
const WAVE_MIN_HEIGHT_RATIO = 0.05
const WAVE_MIN_ALPHA = 0.25
